import React from "react"
import { ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { useAuthState } from "react-firebase-hooks/auth"
import { Box, CircularProgress } from "@mui/material"

import { auth } from "../firebaseAuth"
import { LoadingService } from "react-ui-components/components/LoadingService"
import { initializeLocalization } from "./authLifecycle"
import { tryAnonymousLogin } from "./anonymousLogin"
import { useErrorHandler } from "../hooks/useErrorHandler"

type AuthProviderProps = {
  children: ReactNode
  partial?: boolean
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, partial }) => {
  const [user, loading] = useAuthState(auth)
  const [isInitialized, setIsInitialized] = useState(false)
  const anonymousAttemptedRef = useRef(false)
  const [authError, setAuthError] = useState<unknown>(null)
  const { handleError } = useErrorHandler("AuthProvider")

  useEffect(() => {
    let mounted = true
    const bootstrap = async () => {
      try {
        await initializeLocalization()
      } catch (err) {
        handleError(err, { operation: "initializeLocalization" })
      } finally {
        if (mounted) {
          setIsInitialized(true)
        }
      }
    }

    bootstrap()

    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    // Once we have any user (anonymous or not) the guard below should re-arm, so a later
    // sign-out gets its own anonymous re-login attempt instead of leaving canAccess stuck on
    // false forever (Trello #215 - logout left the app permanently blank).
    if (user) {
      anonymousAttemptedRef.current = false
    }
  }, [user])

  useEffect(() => {
    if (loading || anonymousAttemptedRef.current || user) {
      return
    }

    anonymousAttemptedRef.current = true
    tryAnonymousLogin(auth).catch((err) => {
      handleError(err, { operation: "anonymousSignIn" })
      setAuthError(err)
      // Avoid an infinite loop of retries; surface the error and keep the gate closed.
      LoadingService.setLoading(true, err?.message || err, true)
    })
  }, [loading, user])

  const canAccess = useMemo(() => {
    if (authError && partial) {
      return true
    }

    if (!user) {
      return false
    }
    if (partial) {
      return true
    }
    return !user.isAnonymous
  }, [user, partial, authError])

  if (!isInitialized || !canAccess) {
    // A brief gap is expected while the anonymous session is (re)established after sign-out;
    // render a spinner here instead of null so that gap never reads as a blank/broken app.
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress aria-label="Loading" />
      </Box>
    )
  }

  return <>{children}</>
}
